import React, { useEffect, useState } from 'react';
import { clickable } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const LICENSE_COLORS = {
  'Microsoft 365 Business Premium': { bg: '#ede9fe', color: '#7c3aed', border: '#c4b5fd' },
  'Microsoft 365 Business Standard': { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  'Microsoft 365 Business Basic': { bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' },
  'Exchange Online (Plan 2)': { bg: '#fef3c7', color: '#b45309', border: '#fcd34d' },
  'Exchange Online (Plan 1)': { bg: '#f0fdf4', color: '#15803d', border: '#86efac' },
};

const LICENSE_ORDER = [
  'Microsoft 365 Business Premium',
  'Microsoft 365 Business Standard',
  'Microsoft 365 Business Basic',
  'Exchange Online (Plan 2)',
  'Exchange Online (Plan 1)',
];

function LicenseBadge({ name }) {
  const s = LICENSE_COLORS[name] || { bg: 'var(--surface-2)', color: 'var(--text-2)', border: 'var(--border)' };
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: 'nowrap' }}>
      {name}
    </span>
  );
}

function Avatar({ name }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-light)',
      color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
      {initials}
    </div>
  );
}

export default function ActiveUsersPage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [search, setSearch]     = useState('');
  const [filterLic, setFilterLic] = useState('all');
  const [emailStatus, setEmailStatus] = useState(null); // null | 'sending' | 'ok' | 'err'

  const load = () => {
    setLoading(true); setError(null);
    fetch(`${API}/api/ms/active-users`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const users = data?.users || [];

  const filtered = users.filter(u => {
    if (search && !u.display_name.toLowerCase().includes(search.toLowerCase()) &&
        !u.mail.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterLic !== 'all' && !u.licenses.includes(filterLic)) return false;
    return true;
  });

  const sendEmail = () => {
    setEmailStatus('sending');
    fetch(`${API}/api/ms/active-users/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
      .then(r => r.json())
      .then(d => { setEmailStatus(d.ok ? 'ok' : 'err'); setTimeout(() => setEmailStatus(null), 4000); })
      .catch(() => { setEmailStatus('err'); setTimeout(() => setEmailStatus(null), 4000); });
  };

  const exportCSV = () => {
    const month = data?.month || '';
    const rows = [['Display Name', 'Email']];
    filtered.forEach(u => rows.push([u.display_name, u.mail]));
    const csv = rows.map(r => r.map(v => `"${(v||'').replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `active-users-${month.replace(' ','-')}.csv`;
    a.click();
  };

  const selectStyle = { fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, padding: '6px 10px',
    border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)',
    color: 'var(--text)', cursor: 'pointer', outline: 'none' };

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="card" style={{ cursor: 'pointer' }} {...clickable(() => setFilterLic('all'))} aria-label="Show all licenses">
          <div className="card-title">Total Active Users</div>
          <div className="card-value" style={{ color: 'var(--accent)' }}>{data?.total ?? '—'}</div>
          <div className="card-sub">{data?.month || 'Loading…'}</div>
        </div>
        {LICENSE_ORDER.map(lic => {
          const count = data?.by_license?.[lic];
          if (!count) return null;
          const s = LICENSE_COLORS[lic] || {};
          return (
            <div key={lic} className="card" style={{ cursor: 'pointer', borderLeft: `3px solid ${s.border || 'var(--border)'}` }}
              {...clickable(() => setFilterLic(filterLic === lic ? 'all' : lic))} aria-label={`Filter by ${lic}`}>
              <div className="card-title" style={{ fontSize: 10 }}>{lic}</div>
              <div className="card-value" style={{ color: s.color || 'var(--text)', fontSize: 28 }}>{count}</div>
              <div className="card-sub">{filterLic === lic ? '✓ Filtering' : 'Click to filter'}</div>
            </div>
          );
        })}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="text" placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200, fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, padding: '7px 12px',
            border: '1px solid var(--border)', borderRadius: 7, outline: 'none', background: 'var(--surface)' }} />
        <select style={selectStyle} value={filterLic} onChange={e => setFilterLic(e.target.value)}>
          <option value="all">All licenses</option>
          {LICENSE_ORDER.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {(search || filterLic !== 'all') && (
          <button onClick={() => { setSearch(''); setFilterLic('all'); }}
            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 100, background: 'var(--warn-light)',
              color: 'var(--warn)', border: '1px solid #fde68a', cursor: 'pointer', fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 600 }}>
            ✕ Clear
          </button>
        )}
        <button className="btn btn-secondary" onClick={load} style={{ flexShrink: 0 }}>
          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
          Refresh
        </button>
        {data && (
          <button className="btn btn-secondary" onClick={exportCSV} style={{ flexShrink: 0 }}>
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export CSV
          </button>
        )}
        {data && (
          <button className="btn btn-secondary" onClick={sendEmail} disabled={emailStatus === 'sending'}
            style={{ flexShrink: 0, opacity: emailStatus === 'sending' ? .6 : 1,
              color: emailStatus === 'ok' ? 'var(--ok)' : emailStatus === 'err' ? 'var(--critical)' : undefined }}>
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            {emailStatus === 'sending' ? 'Sending…' : emailStatus === 'ok' ? '✓ Sent' : emailStatus === 'err' ? '✗ Failed' : 'Email'}
          </button>
        )}
      </div>

      {filtered.length !== users.length && !loading && (
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10 }}>
          Showing <strong>{filtered.length}</strong> of <strong>{users.length}</strong> users
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="loading">Loading users from Microsoft 365…</div>
      ) : error ? (
        <div className="empty"><p style={{ color: 'var(--critical)' }}>{error}</p></div>
      ) : filtered.length === 0 ? (
        <div className="empty"><p>No users match your filters.</p></div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '36px 2fr 2fr 1fr', gap: 12, padding: '8px 16px',
            background: 'var(--surface-2)', borderBottom: '1px solid var(--border)',
            fontSize: 10, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            <span>#</span>
            <span>Name</span>
            <span>Email</span>
            <span>License(s)</span>
          </div>
          {filtered.map((u, idx) => (
            <div key={u.upn} style={{ display: 'grid', gridTemplateColumns: '36px 2fr 2fr 1fr', gap: 12,
              padding: '10px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center',
              transition: 'background .1s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <span className="mono" style={{ fontSize: 11, color: 'var(--text-2)' }}>{idx + 1}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <Avatar name={u.display_name} />
                <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.display_name}
                </span>
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {u.mail}
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {u.licenses.map(l => <LicenseBadge key={l} name={l} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-2)', textAlign: 'right' }}>
          Data from Microsoft 365 · {data.month} · cached 10 min
        </div>
      )}
    </div>
  );
}
